import { useCallback, useMemo } from 'react'

import { useQueryClient as useReactQueryClient } from '@tanstack/react-query'
import {
  AIChatSuggestedPrompt,
  CreateAIChatMutation,
  CreateAIChatMutationVariables,
  GetStaffAIChatSessionQuery,
  GetStaffAIChatSessionQueryVariables,
  GetStaffAIChatSessionsQuery,
  GetStaffAIChatSessionsQueryVariables,
  GetStaffAIChatSuggestedPromptsQuery,
  GetStaffAIChatSuggestedPromptsQueryVariables,
  StaffAIChatSessionPreviewFragment,
  UpdateAIChatMutation,
  UpdateAIChatMutationVariables,
} from 'types/graphql'

import { useActiveOrgId } from 'src/hooks/use-active-org-id'
import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

/**
 * Mutation to create a staff AI chat message.
 * Uses the unified createAIChat endpoint with sessionType: Internal.
 */
const CreateStaffAIChat = gql`
  mutation CreateAIChatMutation($input: CreateAIChatInput!) {
    createAIChat(input: $input) {
      id
      response
      questionCreatedAt
      responseCreatedAt
      citations {
        ...AIChatCitationFragment
      }
    }
  }
`

export const useCreateStaffAIChatMutation = () => {
  return useMutation<CreateAIChatMutation, CreateAIChatMutationVariables>({
    mutationDocument: CreateStaffAIChat,
  })
}

/**
 * Mutation to rate a staff AI chat message (thumbs up/down).
 */
const UpdateStaffAIChat = gql`
  mutation UpdateAIChatMutation($id: Int!, $input: UpdateAIChatInput!) {
    updateAIChat(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateStaffAIChatMutation = () => {
  return useMutation<UpdateAIChatMutation, UpdateAIChatMutationVariables>({
    mutationDocument: UpdateStaffAIChat,
  })
}

/**
 * Query to get personalized prompt suggestions for staff AI chat.
 */
const GetStaffAIChatSuggestedPrompts = gql`
  query GetStaffAIChatSuggestedPromptsQuery {
    getAIChatSuggestedPrompts(sessionType: Internal) {
      prompt
      category
    }
  }
`

export const useGetStaffAIChatSuggestedPromptsQuery = () => {
  const { data, ...rest } = useQuery<
    GetStaffAIChatSuggestedPromptsQuery,
    GetStaffAIChatSuggestedPromptsQueryVariables
  >({
    queryDocument: GetStaffAIChatSuggestedPrompts,
    queryKey: [QueryKey.StaffAIChatSuggestedPrompts],
    variables: {},
  })

  const suggestedPrompts: AIChatSuggestedPrompt[] = useMemo(
    () => data?.getAIChatSuggestedPrompts ?? [],
    [data]
  )

  return {
    ...rest,
    suggestedPrompts,
  }
}

/**
 * Query to get the current user's past staff AI chat sessions.
 */
const GetStaffAIChatSessions = gql`
  query GetStaffAIChatSessionsQuery($input: GetStaffAIChatSessionsInput!) {
    getStaffAIChatSessions(input: $input) {
      ...StaffAIChatSessionPreviewFragment
    }
  }
`

export const useGetStaffAIChatSessionsQuery = () => {
  const organizationId = useActiveOrgId()
  const { data, ...rest } = useQuery<
    GetStaffAIChatSessionsQuery,
    GetStaffAIChatSessionsQueryVariables
  >({
    queryDocument: GetStaffAIChatSessions,
    queryKey: [QueryKey.StaffAIChatSessions, organizationId],
    variables: { input: { organizationId: organizationId ?? -1 } },
    enabled: !!organizationId,
  })

  const sessions: StaffAIChatSessionPreviewFragment[] = useMemo(
    () => data?.getStaffAIChatSessions ?? [],
    [data]
  )

  return {
    ...rest,
    sessions,
    organizationId,
  }
}

/**
 * Hook to invalidate the staff AI chat sessions query.
 * Used when a new message is sent to refresh the sessions list.
 */
export const useInvalidateStaffAIChatSessions = () => {
  const queryClient = useReactQueryClient()
  const organizationId = useActiveOrgId()
  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.StaffAIChatSessions, organizationId],
    })
  }, [queryClient, organizationId])
}

/**
 * Creates an optimistic session preview for display in the sidebar.
 * Uses a negative ID within GraphQL Int range to distinguish from real sessions.
 * Note: Date.now() exceeds GraphQL Int max (~2.1e9), so we use modulo to stay in range.
 */
const createOptimisticSessionPreview = (question: string): StaffAIChatSessionPreviewFragment => {
  const now = Date.now()
  return {
    __typename: 'AIChatSession',
    id: -(now % 2147483647), // Negative ID within GraphQL Int range
    sessionType: 'Internal',
    startedAt: new Date().toISOString(),
    previewChat: {
      __typename: 'AIChat',
      id: -((now % 2147483647) + 1), // Different ID to avoid collision
      question,
      questionCreatedAt: new Date().toISOString(),
    },
  }
}

/**
 * Hook to add an optimistic session to the sessions list.
 * Call this immediately when the first message is sent in a new chat
 * to update the sidebar before the mutation completes.
 */
export const useAddOptimisticSession = () => {
  const queryClient = useReactQueryClient()
  const organizationId = useActiveOrgId()
  return useCallback(
    (question: string) => {
      const optimisticSession = createOptimisticSessionPreview(question)
      queryClient.setQueryData<GetStaffAIChatSessionsQuery>(
        [QueryKey.StaffAIChatSessions, organizationId],
        (oldData) => {
          if (!oldData?.getStaffAIChatSessions) {
            return {
              getStaffAIChatSessions: [optimisticSession],
            }
          }
          // Add the optimistic session at the beginning (most recent)
          return {
            getStaffAIChatSessions: [optimisticSession, ...oldData.getStaffAIChatSessions],
          }
        }
      )
    },
    [queryClient, organizationId]
  )
}

/**
 * Query to get a specific staff AI chat session with all messages.
 */
const GetStaffAIChatSession = gql`
  query GetStaffAIChatSessionQuery($id: Int!) {
    aiChatSession(id: $id) {
      ...StaffAIChatSessionWithMessagesFragment
    }
  }
`

export const useGetStaffAIChatSessionQuery = (sessionId: number | null) => {
  const { data, ...rest } = useQuery<
    GetStaffAIChatSessionQuery,
    GetStaffAIChatSessionQueryVariables
  >({
    queryDocument: GetStaffAIChatSession,
    queryKey: [QueryKey.StaffAIChatSession, sessionId],
    variables: { id: sessionId ?? 0 },
    enabled: !!sessionId,
  })

  return {
    ...rest,
    session: data?.aiChatSession ?? null,
  }
}
